import type { BaseComponentProps } from "@neru/types";

export interface RadioProps extends BaseComponentProps {
  checked?: boolean;
  disabled?: boolean;
  label?: string;
  name?: string;
  onClick?: (e: React.ChangeEvent<HTMLInputElement>) => void | Promise<void>;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  required?: boolean;
  value?: string;
}

export const Radio = ({
  checked = false,
  className = "",
  "data-testid": testId = "radio",
  disabled = false,
  label,
  name,
  onClick,
  onKeyDown,
  required = false,
  value = "true",
  ...props
}: RadioProps) => {
  const radioStyles = `
    appearance-none
    cursor-pointer
    h-5
    w-5
    border-2
    rounded-full
    grid
    place-content-center
    focus:outline-none`;

  const radioColorStyles = disabled
    ? `disabled:cursor-not-allowed
      disabled:border-primary-300
      disabled:bg-primary-100
      dark:disabled:border-primary-600
      dark:disabled:bg-primary-800`
    : `border-primary-500
      hover:border-accent-check-faintLight
      checked:bg-accent-check-text
      checked:border-accent-check-primary
      checked:group-hover:border-accent-check-faintLight
      dark:border-primary-400
      dark:group-hover:border-accent-check-faintDark
      dark:checked:bg-accent-check-primary
      dark:checked:border-accent-check-text
      dark:checked:group-hover:border-accent-check-faintDark`;

  const checkStyles = `
    before:content-[' ']
    before:rounded-full
    before:h-2.5
    before:scale-0
    before:w-2.5
    before:transition-transform
    before:duration-100
    before:ease-in-out
    checked:before:scale-100`;

  const checkColorStyles = disabled
    ? "before:bg-primary-300 dark:before:bg-primary-600 before:cursor-not-allowed"
    : `before:bg-accent-check-primary
      group-hover:before:bg-accent-check-faintLight
      dark:before:bg-accent-check-text
      dark:group-hover:before:bg-accent-check-faintDark`;

  return (
    <label
      className={`
        group flex cursor-pointer items-center text-primary-1000

        dark:text-primary-0

        ${className}
      `}
    >
      <input
        checked={checked}
        className={[
          radioStyles,
          radioColorStyles,
          checkStyles,
          checkColorStyles,
        ].join(" ")}
        data-testid={testId}
        disabled={disabled}
        name={name}
        onChange={onClick}
        onKeyDown={onKeyDown}
        required={required}
        type="radio"
        value={value}
        {...props}
      />
      {label ? <span className="ml-2">{label}</span> : null}
    </label>
  );
};
